import { Component } from 'preact';
import { sum } from 'd3-array';

import { convertWindBearing8 } from '../../utils/convert-wind-bearing';
import { getRainColourInterpolator, getTemperatureColourInterpolator, getWindColourInterpolator } from '../../utils/interpolators';
import RainDrop from '../rain-drop';
import Button from '../button';
import { formatRainfall, formatWindGust, getDateTimeFormatConverter } from '../../utils/formatters';
import style from './style.scss';
import { convertRainfall } from '../../utils/unit-converters';
import { RAIN_UNIT } from '../../utils/constants';

/**
 * The HourlyList component displays a list of hourly observations, in a vertical arrangement.
 * It is used instead of the HourlyChartWrapper component on smaller screens where there is
 * insufficient width to draw a chart.
 *
 * NOTE: data passed down is already converted into the units set by the user.
 *
 * props:
 * - data: An array of objects, each containing hourly observation data
 * - descending: optional - set to true to sort in descending order
 */
class HourlyList extends Component {

	constructor(props, context) {
		super(props, context);

		this.units = context.userSettings.units;
		this.isExpandedByDefault = context.userSettings.ui.hourlyListExpanded;

		this.state = {
			isExpanded: this.isExpandedByDefault
		};

		// Get a light-colour temperature interpolator
		this.getTemperatureColour = getTemperatureColourInterpolator(true, this.units.temperature);

		const windColour = getWindColourInterpolator(this.units.speed);
		const rainColour = getRainColourInterpolator(convertRainfall(15, this.units.rain));

		this.getWindColour = w => windColour(w, this.units.speed);
		this.getRainColour = r => rainColour(r, this.units.rain);

		const standardTimeFormatter = getDateTimeFormatConverter('%Y-%m-%d %H:%M:%S', '%-I %p');

		const timeFormatter = t => {
			return t.split(' ')[1] === '23:59:59'
				? '12 am'
				: standardTimeFormatter(t);
		};

		this.formatters = {
			time: timeFormatter,
			rainfall: r => formatRainfall(r, this.units.rain, true)
		};

		this.toggleNumItems = this.toggleNumItems.bind(this);
	}

	isExpanded() {
		return this.props.alwaysExpanded || this.state.isExpanded;
	}

	getRainTotal() {
		const validRainfalls = this.props.data
			.map(ob => ob.hourlyRainfall)
			.filter(r => r !== null);

		if (validRainfalls.length === 0) {
			return 'n/a';
		}

		const total = sum(validRainfalls);

		return this.formatters.rainfall(total) + ' ' + this.units.rain;
	}

	render(props) {
		if (!Array.isArray(props.data)) {
			return null;
		}

		const formattedData = this.formatData(props.data);

		if (props.descending) {
			formattedData.reverse(); // display most recent at the top
		}

		return (
			<div>
				{
					props.descending
						? <p class={style.hourly_list_note}>Descending order (most recent at the top)</p>
						: null
				}
				<ul class={style.hourly_list}>
					<li class={style.hourly_list_head}>
						<div class={style.hourly_list_time_column}>Time</div>
						<div class={style.hourly_list_temp_column}>Temp (°{this.units.temperature})</div>
						<div class={style.hourly_list_rain_column}>Rain ({this.units.rain})</div>
						<div class={style.hourly_list_wind_column}>Wind</div>
						<div class={style.hourly_list_gust_column}>Gust ({this.units.speed})</div>
					</li>
					{
						formattedData.slice(0, this.isExpanded() ? formattedData.length : 12).map(ob => (
							<li class={style.hourly_list_row} key={ob.key}>

								<div class={style.hourly_list_time_column}>
									{ this.formatters.time(ob.timestamp) }
								</div>

								<div class={style.hourly_list_temp_column}>
									<span class={style.hourly_list_temperature_cell}
										  style={{ backgroundColor: this.getTemperatureColour(ob.temperature) }}>
										{ ob.temperature }
									</span>
								</div>

								<div class={style.hourly_list_rain_column}>
									<span class={style.hourly_list_rain_cell}>
										{
											ob.hourlyRainfall === null
												? 'n/a'
												: (ob.hourlyRainfall > 0 ? this.formatters.rainfall(ob.hourlyRainfall) : '-')
										}
										<span class={style.hourly_list_raindrop}>
										{
											ob.hourlyRainfall > 0
												? <RainDrop fill={this.getRainColour(ob.hourlyRainfall)} size={this.getRainDropSize(ob.hourlyRainfall)} />
												: null
										}
										</span>
									</span>
								</div>

								<div class={style.hourly_list_wind_column}>
									{ ob.windDirection }
								</div>

								<div class={style.hourly_list_gust_column}>
									<span class={style.hourly_list_wind_cell}
									      style={{ backgroundColor: this.getWindColour(ob.hourlyHighWindGust) }}>
										{ formatWindGust(ob.hourlyHighWindGust, this.units.speed, true, true) }
									</span>
								</div>

							</li>
						))
					}
				</ul>
				{
					this.isExpanded()
						? <p class={style.hourly_list_note}>Rain Total = { this.getRainTotal() }</p>
						: null
				}
				{ this.renderToggleButton() }
			</div>
		);
	}

	renderToggleButton() {
		if (this.props.alwaysExpanded) {
			return null;
		}

		return (
			<Button wide clickHandler={this.toggleNumItems}>
				{ this.isExpanded() ? 'Show less' : 'Show more' }
			</Button>
		);
	}

	toggleNumItems() {
		this.setState(state => ({ isExpanded: !state.isExpanded }));
	}

	getRainDropSize(rain) {
		const rainMm = this.units.rain === RAIN_UNIT.IN ? rain * 25.4 : rain;
		return Math.min(32, 10 + rainMm);
	}

	formatData(data) {
		return data.map((ob, index) => {
			const windDirection = ob.hourlyHighWindGust > 0 ? convertWindBearing8(ob.windBearing) : '-';

			return {
				...ob,
				windDirection,
				key: index
			};
		});
	}

}

export default HourlyList;



// WEBPACK FOOTER //
// ./components/hourly-list/index.js